/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Document } from 'docx';
import { DownloadMultiScope } from '../../types/download';
import { SurveyInitiative } from '../../types/survey';
import { DataPeriods } from '../../types/universalTracker';
import { formatDate } from '../../utils/date';
import { getStartDate } from '../../utils/surveyData';
import { sasbLogoBase64 } from './base64Logo';
import { paragraphArray } from './document-structure';
import { SimpleReportGenerator } from './simple-report-generator';
import { ReportGeneratorFn } from './types';

export const SASBReportGenerator: ReportGeneratorFn<Document> = async (
  survey: SurveyInitiative,
  reportData,
  downloadScope: DownloadMultiScope
): Promise<Document> => {

  const generator = await SimpleReportGenerator(survey, reportData, 'sasb', sasbLogoBase64, 'B73238', downloadScope);

  const reportingPeriod = survey.period ?? DataPeriods.Yearly;
  const reportingStartDate = formatDate(getStartDate(survey.effectiveDate, reportingPeriod), 'MMMM YYYY');
  const reportingEndDate = formatDate(survey.effectiveDate, 'MMMM YYYY');

  const companyName = survey.initiative.name;
  const reportingPeriodStr = reportingPeriod === DataPeriods.Monthly ? reportingEndDate : `${reportingStartDate} to ${reportingEndDate}`;

  const intro = paragraphArray([
    'For readers that want to compare our sustainability performance against our peers, we include this appendix. ' +
    'The Sustainability Accounting Standards Board (SASB) is an independent standards-setting organization that supports ' +
    'reporting standards for companies to identify, manage and communicate sustainability information to their stakeholders. ' +
    'This 2021 SASB Report is our first disclosure pursuant to the SASB standards, and as such, our disclosures on these ' +
    'topics will evolve and mature with time.',

    '',

    'SASB guidelines differ depending on the industry. For more information on SASB, visit www.sasb.org. All data covers ' +
    `the period of ${reportingPeriodStr}. It includes all of ${companyName} wholly owned entities.`,

    '',

    'Where noted, in accordance with SASB Standards Application Guidance section 2.2, certain information is not ' +
    'included in this appendix, to the extent the information is not considered material; is privileged or confidential; '+
    'or is not currently collected in a manner wholly correlative with the related SASB metric. Where possible we have '+
    'shared alternative information that we believe may be insightful.'
  ])

  return generator.generate({
    getStyles: generator.getStyles,
    getLogos: generator.getLogos,
    getHeading: generator.getHeading,
    getSubHeading: () => [intro],
    getTables: generator.getTables,
    getAppendix: generator.getAppendix,
  });
}

